import React from 'react';
import {
  Alert,
  Box,
  Divider,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { TransactionDetails } from '../schemas/transaction.schemas';

type FailureDetails = NonNullable<
  NonNullable<TransactionDetails['metadata']>['failureDetails']
>;

type TextDetailItem = {
  kind: 'text';
  label: string;
  value: string;
};

type LinkDetailItem = {
  kind: 'link';
  label: string;
  href: string;
  linkText: string;
};

type DetailItem = TextDetailItem | LinkDetailItem;

const hasDisplayValue = (value?: string | null): value is string =>
  Boolean(value?.trim());

const formatCardLabel = (card: FailureDetails['card']): string | undefined => {
  if (!card?.cardType && !card?.last4) return undefined;
  const type = card.cardType ?? 'Card';
  const last4 = card.last4 ? ` •••• ${card.last4}` : '';
  return `${type}${last4}`;
};

const processorLogLinkLabel = (
  provider: FailureDetails['provider'],
): string => {
  if (provider === 'stripe') return 'View Stripe request log';
  if (provider === 'braintree') return 'View in Braintree';
  return 'View processor log';
};

const TextDetailRow = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

const LinkDetailRow = ({
  label,
  href,
  linkText,
}: {
  label: string;
  href: string;
  linkText: string;
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <MuiLink
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      variant="body2"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      {linkText}
      <OpenInNewIcon sx={{ fontSize: 14 }} />
    </MuiLink>
  </Box>
);

const isProcessorDecline = (failureDetails: FailureDetails): boolean =>
  Boolean(failureDetails.provider);

const resolvePanelTitle = (
  stage: string | null | undefined,
  failureDetails: FailureDetails,
): string => {
  if (stage === 'license' || stage === 'bundle_expansion') {
    return 'Fulfillment failure details';
  }
  if (isProcessorDecline(failureDetails)) {
    return 'Payment failure details';
  }
  return 'Failure details';
};

const buildDetailItems = (failureDetails: FailureDetails): DetailItem[] => {
  const processorDecline = isProcessorDecline(failureDetails);
  const cardLabel = formatCardLabel(failureDetails.card);
  const avsParts = [
    failureDetails.avs?.street ? `Street: ${failureDetails.avs.street}` : null,
    failureDetails.avs?.postalCode
      ? `ZIP: ${failureDetails.avs.postalCode}`
      : null,
    failureDetails.avs?.cvv ? `CVV: ${failureDetails.avs.cvv}` : null,
  ].filter(Boolean);
  const avsLabel = avsParts.join(' · ');
  const errorType = failureDetails.declineType?.replace(/_/g, ' ');
  const recordIdLabel =
    failureDetails.provider === 'braintree'
      ? 'Braintree record ID'
      : 'Stripe request ID';

  const items: DetailItem[] = [];

  const pushText = (label: string, value?: string | null) => {
    if (hasDisplayValue(value)) {
      items.push({ kind: 'text', label, value });
    }
  };

  pushText('Failure point', failureDetails.failurePoint);
  pushText(
    processorDecline ? 'Processor message' : 'Error message',
    failureDetails.processorMessage,
  );

  if (processorDecline) {
    pushText('Provider', failureDetails.provider);
    pushText('Error code', failureDetails.errorCode);
    pushText('Decline code', failureDetails.declineCode);
    pushText('Network decline code', failureDetails.networkDeclineCode);
    pushText('Advice', failureDetails.adviceCode);
    pushText('Error type', errorType);
    pushText(recordIdLabel, failureDetails.requestId);

    if (hasDisplayValue(failureDetails.requestLogUrl)) {
      items.push({
        kind: 'link',
        label: 'Processor log',
        href: failureDetails.requestLogUrl!,
        linkText: processorLogLinkLabel(failureDetails.provider),
      });
    }

    pushText('Gateway rejection', failureDetails.gatewayRejectionReason);
    pushText('Card', cardLabel);
    pushText('AVS / CVV', avsLabel);
  }

  return items;
};

const TransactionFailureDetailsPanel: React.FC<{
  failureDetails: FailureDetails;
  stage?: string | null;
}> = ({ failureDetails, stage }) => {
  const detailItems = buildDetailItems(failureDetails);
  const panelTitle = resolvePanelTitle(stage, failureDetails);

  return (
    <Alert severity="error">
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {panelTitle}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        {failureDetails.summary}
      </Typography>
      {detailItems.length > 0 && (
        <Stack spacing={1.25}>
          {detailItems.map((item, index) => (
            <React.Fragment key={`${item.kind}-${item.label}`}>
              {index > 0 && <Divider flexItem />}
              {item.kind === 'text' ? (
                <TextDetailRow label={item.label} value={item.value} />
              ) : (
                <LinkDetailRow
                  label={item.label}
                  href={item.href}
                  linkText={item.linkText}
                />
              )}
            </React.Fragment>
          ))}
        </Stack>
      )}
    </Alert>
  );
};

export default TransactionFailureDetailsPanel;
